import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import CircularProgress from '@mui/material/CircularProgress';
import propTypes from 'prop-types';
import { isEqual } from 'lodash';
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
const MySwal = withReactContent(Swal);

const useStyles = makeStyles((theme) => ({
  templateMessage: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
  templateMessagesCheckboxesContainer: {
    marginTop: theme.spacing(2),
  },
  circularProgress: {
    marginRight: theme.spacing(2),
    verticalAlign: 'middle',
  },
  textField: {
    marginTop: theme.spacing(3),
  },
  textArea: {
    marginTop: theme.spacing(3),
    minHeight: '100px',
    padding: '5px',
    border: '1px solid rgba(0, 0, 0, 0.42)',
    fontSize: 'inherit',
    font: 'inherit',
  },
  cancelButton: {
    marginLeft: '10px',
  },
  switch: {
    borderRadius: '3rem',
    width: 'min-content',
    border: '1px solid #eee',
    marginBottom: 16,
    cursor: 'pointer',
  },
  leftSwitch: {
    paddingLeft: 16,
    paddingRight: 16,
    borderRadius: '3rem 0 0 3rem',
  },
  rightSwitch: {
    paddingRight: 16,
    paddingLeft: 16,
    borderRadius: '0 3rem 3rem 0',
  },
  activeSwitch: {
    backgroundColor: theme.palette.primary.main,
  },
}));

function TemplateMessage({ index, mode, templateMessage, onSubmit, onCancel, onDelete, ...rest }) {
  const profile = useSelector((state) => state.firebase.profile);
  const organization = useSelector((state) => state.app.organization);
  const classes = useStyles();
  // use default values to avoid uncontrolled components
  const defaultValues = {
    value: '',
    message: '',
  };

  // copy templateMessage to compare for later subcription updates
  const [templateMessageCopy, setTemplateMessageCopy] = useState({ ...templateMessage });
  // clone object to have a local copy and work on it
  const [_templateMessage, _setTemplateMessage] = useState(
    mode === 'create' ? defaultValues : { ...templateMessage },
  );
  // submit button is always show on creation mode and only if there are changes on update mode
  const showSubmitButton =
    mode === 'create' || isEqual(templateMessage, _templateMessage) === false;
  // is submit in progress?
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    if (mode !== 'update') return;
    // if there are changes then refresh it
    if (!isEqual(templateMessage, {}) && !isEqual(templateMessage, templateMessageCopy)) {
      setTemplateMessageCopy({ ...templateMessage });
      _setTemplateMessage({ ...templateMessage });
    }
  }, [mode, templateMessage, templateMessageCopy]);

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const key = target.name;

    _setTemplateMessage({
      ..._templateMessage,
      [key]: value,
    });
  };

  const handleSubmitCallback = () => {
    setInProgress(false);
    if (mode === 'create') _setTemplateMessage(defaultValues);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setInProgress(true);
    onSubmit(
      { ..._templateMessage, createdAt: moment().toDate() },
      mode,
      index,
      handleSubmitCallback,
    );
  };

  const handleDeleteCallback = () => {
    setInProgress(false);
  };

  const handleDelete = (event) => {
    event.preventDefault();
    if (mode === 'create') onCancel();
    else {
      MySwal.fire({
        text: '¿Esta seguro que quiere borrarlo?',
        showConfirmButton: true,
        showDenyButton: true,
        icon: 'error',
        input: templateMessage.reply && 'checkbox',
        inputPlaceholder: templateMessage.reply && 'Borrar replica',
        inputValue: true,
      }).then((result) => {
        if (result.isConfirmed) {
          setInProgress(true);
          onDelete(_templateMessage, handleDeleteCallback, result.value);
        }
      });
    }
  };

  const handleSwitch = (global) => {
    _setTemplateMessage({
      ..._templateMessage,
      global,
      organizationId: global ? '' : organization.id,
    });
  };

  return (
    <Paper {...rest} className={classes.templateMessage} elevation={3}>
      <form onSubmit={handleSubmit}>
        {profile && profile.claims && profile.claims.includes('admin') && (
          <Box display="flex" className={classes.switch}>
            <Box
              className={`${classes.leftSwitch} ${_templateMessage.global && classes.activeSwitch}`}
              onClick={() => handleSwitch(true)}
            >
              <Typography>Global</Typography>
            </Box>
            <Box
              className={`${classes.rightSwitch} ${
                !_templateMessage.global && classes.activeSwitch
              }`}
              onClick={() => handleSwitch(false)}
            >
              <Typography>Local</Typography>
            </Box>
          </Box>
        )}
        <FormControl fullWidth>
          <TextField
            required
            id={`type-value-${index}`}
            label="Titulo"
            name="value"
            value={_templateMessage.value}
            onChange={handleInputChange}
            inputProps={{ style: { textTransform: 'uppercase' } }}
          />
        </FormControl>
        <FormControl fullWidth>
          <TextareaAutosize
            className={classes.textArea}
            placeholder="Mensaje"
            name="message"
            value={_templateMessage.message}
            onChange={handleInputChange}
          />
        </FormControl>
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          className={classes.templateMessagesCheckboxesContainer}
        >
          <Grid>
            {inProgress && <CircularProgress className={classes.circularProgress} />}
            {!inProgress && showSubmitButton && (
              <Button type="submit" variant="contained" color="primary">
                {mode === 'create' ? 'Crear' : 'Guardar'}
              </Button>
            )}
            {!inProgress && (
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={handleDelete}
                className={showSubmitButton ? classes.cancelButton : ''}
              >
                {mode === 'create' ? 'Cancelar' : 'Borrar'}
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
}

const Props = {
  mode: propTypes.oneOf(['create', 'update']),
  index: propTypes.number,
  onSubmit: propTypes.func,
  onCancel: propTypes.func,
  targetTypeDescription: propTypes.string,
  templateMessage: propTypes.object,
  onDelete: propTypes.func,
  originType: propTypes.number,
};

TemplateMessage.propTypes = Props;

export default TemplateMessage;
